import { ClearOutlined } from '@ant-design/icons'
import { Button, DatePicker, Form, InputNumber, Select, Table } from 'antd'
import { useWatch } from 'antd/es/form/Form'
import { FC, useEffect, useMemo, useRef } from 'react'
import './filters-form.css'
import useFiltersForm from '../../../../../../../hooks/forms/use-filters-form'
import { outputDateFormat } from '../../../../../../../constants/constants'
import { receiptsStatus } from '../../../../../../../constants/entity-enums/order-request-status'
import MaterialTypes from '../../../../../../../components/form-items/material-types/material-types'
import { useGetMaterialsQuery } from '../../../../../../../store/api/materials-api'
import { useGetUsersQuery } from '../../../../../../../store/api/users-api'
import { useGetContractorsQuery } from '../../../../../../../store/api/contractors-api'
import { QueryParams } from '../../../../../../../types/strapi/strapi'

const suppliersParams: QueryParams = {
    filters: { type: { name: { $contains: 'Поставщик материала' } } },
}

interface FiltersFormProps {}

const FiltersForm: FC<FiltersFormProps> = () => {
    const { form, onSearch, onClear } = useFiltersForm()
    const materialTypeDocId = useWatch('materialTypeDocId', form)

    const { data: materialsData } = useGetMaterialsQuery({
        ...(materialTypeDocId
            ? { filters: { material_type: { documentId: { $eq: materialTypeDocId } } } }
            : {}),
        fields: ['width', 'documentId'],
        pagination: { pageSize: 1000, page: 1 },
    })

    const { data: usersData } = useGetUsersQuery({
        filters: {
            user_position: {
                name: { $in: ['Менеджер', 'Помощник менеджера'] },
            },
        },
    })

    const { data: contractors } = useGetContractorsQuery(suppliersParams)

    const widthSelectOptions = useMemo(() => {
        const widths = materialsData?.data.map(item => item.width) || []
        const unique = [...new Set(widths)].sort((a, b) => a - b)

        return unique.map(width => ({ value: width, label: String(width) }))
    }, [materialsData])

    const initiatorOptions = useMemo(
        () =>
            usersData?.map(item => ({
                value: item.documentId,
                label: item.username,
            })) || [],
        [usersData]
    )

    const supplierOptions = useMemo(
        () =>
            contractors?.data.map(item => ({
                value: item.documentId,
                label: item.name,
            })) || [],
        [contractors]
    )

    const prevMaterialTypeDocIdRef = useRef<string | undefined>()

    useEffect(() => {
        const prev = prevMaterialTypeDocIdRef.current
        if (prev !== undefined && prev !== materialTypeDocId) {
            form.setFieldsValue({ materialWidth: undefined })
        }
        prevMaterialTypeDocIdRef.current = materialTypeDocId
    }, [materialTypeDocId, form])

    return (
        <Form
            form={form}
            component={false}
            variant={'filled'}
            onFieldsChange={() => {
                onSearch()
            }}
        >
            <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1}>
                    <Form.Item name={'supplierDocId'} noStyle>
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp='label'
                            style={{ width: '100%' }}
                            placeholder={'Поставщик'}
                            options={supplierOptions}
                            popupMatchSelectWidth={false}
                            dropdownStyle={{ minWidth: 200 }}
                        />
                    </Form.Item>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                    <Form.Item name={'receiptStatus'} noStyle>
                        <Select
                            mode='tags'
                            style={{ width: '100%' }}
                            placeholder={'Статус'}
                            options={receiptsStatus.map(item => ({ value: item, label: item }))}
                        />
                    </Form.Item>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                    <Form.Item name={'createdAt'} noStyle>
                        <DatePicker placeholder={'Дата создания'} format={outputDateFormat} />
                    </Form.Item>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} />
                <Table.Summary.Cell index={5}>
                    <MaterialTypes
                        name={'materialTypeDocId'}
                        rules={[]}
                        noStyle
                        popupMatchSelectWidth={false}
                        dropdownStyle={{ minWidth: 200 }}
                    />
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6}>
                    <Form.Item name={'materialWidth'} noStyle>
                        <Select
                            allowClear
                            showSearch
                            placeholder={'Ширина'}
                            style={{ width: '100%' }}
                            options={widthSelectOptions}
                            popupMatchSelectWidth={false}
                            dropdownStyle={{ minWidth: 80 }}
                        />
                    </Form.Item>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7}>
                    <Form.Item name={'amount'} noStyle>
                        <InputNumber
                            controls={false}
                            placeholder={'Кол-во'}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8}>
                    <Form.Item name={'initiatorDocId'} noStyle>
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp='label'
                            style={{ width: '100%' }}
                            placeholder={'Инициатор'}
                            options={initiatorOptions}
                            popupMatchSelectWidth={false}
                            dropdownStyle={{ minWidth: 160 }}
                        />
                    </Form.Item>
                </Table.Summary.Cell>
                <Table.Summary.Cell className={'actions-cell'} index={9}>
                    <Button
                        size={'small'}
                        icon={<ClearOutlined />}
                        title={'Сбросить фильтры'}
                        onClick={onClear}
                    >
                        Сброс
                    </Button>
                </Table.Summary.Cell>
            </Table.Summary.Row>
        </Form>
    )
}

export default FiltersForm
